import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, Users, Calendar, Shield, Check, Copy, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import confetti from "canvas-confetti";

interface Draw {
  id: string;
  created_at: string;
  draw_title: string;
  min_value: number;
  max_value: number;
  winning_number: number;
  verification_hash: string;
  viewer_count: number;
  organizer_name: string | null;
}

const SharedDraw = () => {
  const { shareCode } = useParams<{ shareCode: string }>();
  const [draw, setDraw] = useState<Draw | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);

  useEffect(() => {
    if (!shareCode) return;

    const fetchDraw = async () => {
      const { data, error } = await supabase
        .from('draws')
        .select('*')
        .eq('share_url', shareCode)
        .single();

      if (error || !data) {
        toast.error('Draw not found');
        setLoading(false);
        return;
      }

      setDraw(data as Draw);
      setViewerCount(data.viewer_count || 1);
      
      // Increment viewer count
      await supabase
        .from('draws')
        .update({ viewer_count: (data.viewer_count || 0) + 1 })
        .eq('id', data.id);

      setLoading(false);

      // Fire confetti on load
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    };

    fetchDraw();

    // Subscribe to viewer count updates
    const channel = supabase
      .channel(`draw-${shareCode}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'draws',
          filter: `share_url=eq.${shareCode}`
        },
        (payload) => {
          if (payload.new.viewer_count) {
            setViewerCount(payload.new.viewer_count);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [shareCode]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCertificate = () => {
    if (!draw) return;

    const certificateHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 40px auto;
              padding: 40px;
              border: 3px solid #0066CC;
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            }
            h1 { color: #0066CC; text-align: center; margin-bottom: 30px; }
            .winner { font-size: 72px; color: #00CCCC; text-align: center; font-weight: bold; margin: 30px 0; }
            .details { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .detail-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee; }
            .label { font-weight: bold; color: #666; }
            .badge { display: inline-block; background: #0066CC; color: white; padding: 4px 12px; border-radius: 12px; font-size: 12px; }
            .hash { font-family: monospace; font-size: 10px; color: #999; word-break: break-all; }
          </style>
        </head>
        <body>
          <h1>🏆 Official Draw Certificate 🏆</h1>
          <div class="winner">#${draw.winning_number}</div>
          <div class="details">
            <div class="detail-row">
              <span class="label">Draw Range:</span>
              <span>${draw.min_value} - ${draw.max_value}</span>
            </div>
            <div class="detail-row">
              <span class="label">Timestamp:</span>
              <span>${new Date(draw.created_at).toLocaleString()}</span>
            </div>
            <div class="detail-row">
              <span class="label">Draw ID:</span>
              <span style="font-family: monospace;">${draw.id.substring(0, 8)}</span>
            </div>
            <div class="detail-row">
              <span class="label">Share Code:</span>
              <span>${shareCode}</span>
            </div>
            <div class="detail-row">
              <span class="label">Total Viewers:</span>
              <span>${viewerCount}</span>
            </div>
          </div>
          <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <div class="label" style="margin-bottom: 8px;">Verification Hash:</div>
            <div class="hash">${draw.verification_hash}</div>
          </div>
          <p style="text-align: center; color: #666; font-size: 12px; margin-top: 30px;">
            <span class="badge">Cryptographically Secure</span><br><br>
            Generated by RandomGen (numbers-generator.com)<br>
            This certificate serves as proof of a fair, unbiased random draw.
          </p>
        </body>
      </html>
    `;

    const blob = new Blob([certificateHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `draw-certificate-${draw.winning_number}.html`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Certificate downloaded!');
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="animate-pulse">Loading draw...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!draw) {
    return (
      <Layout>
        <SEO 
          title="Draw Not Found" 
          description="The requested draw could not be found. Create your own cryptographically secure random draw on RandomGen."
        />
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-4">Draw Not Found</h1>
            <p className="text-muted-foreground mb-6">
              This draw doesn't exist or has been removed.
            </p>
            <Button onClick={() => window.location.href = '/drawings'}>
              Create New Draw
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO 
        title={`Draw Result: #${draw.winning_number} - RandomGen`}
        description={`Official draw result: #${draw.winning_number} from range ${draw.min_value}-${draw.max_value}. Cryptographically verified and fair.`}
      />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Hero Result */}
          <Card className="p-8 md:p-12 bg-gradient-to-br from-primary/10 to-accent/10 border-2 border-primary/20">
            <div className="text-center space-y-6">
              <Badge className="text-sm px-4 py-1 bg-accent/10 text-accent border-accent/20">
                <Users className="w-3 h-3 mr-1" />
                {viewerCount} {viewerCount === 1 ? 'viewer' : 'viewers'}
              </Badge>
              
              <div>
                <Trophy className="w-16 h-16 text-accent mx-auto mb-4" />
                <h1 className="text-3xl md:text-4xl font-semibold text-muted-foreground mb-4">
                  And the winner is...
                </h1>
                <div className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-4">
                  #{draw.winning_number}
                </div>
                <Badge className="text-lg px-6 py-2 bg-accent/10 text-accent border-accent/20">
                  <Shield className="w-4 h-4 mr-2" />
                  Verified Draw
                </Badge>
              </div>
            </div>
          </Card>

          {/* Draw Details */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Draw Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Range</span>
                <span className="font-medium">{draw.min_value} - {draw.max_value}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Draw Time</span>
                <span className="font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(draw.created_at).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Draw ID</span>
                <span className="font-mono text-sm">{draw.id.substring(0, 8)}</span>
              </div>
              {draw.organizer_name && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">Organized by</span>
                  <span className="font-medium">{draw.organizer_name}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Verification */}
          <Card className="p-6 bg-muted/30">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Cryptographic Verification
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              This draw was generated using a cryptographically secure random number generator 
              and is verified with a SHA-256 hash for transparency.
            </p>
            <div className="bg-background p-3 rounded border font-mono text-xs break-all">
              {draw.verification_hash}
            </div>
          </Card>

          {/* Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="gap-2"
              onClick={copyLink}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Link Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Share Link
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              className="gap-2"
              onClick={downloadCertificate}
            >
              <Download className="w-4 h-4" />
              Download Certificate
            </Button>
          </div>

          {/* Create New Draw CTA */}
          <div className="text-center pt-6">
            <p className="text-muted-foreground mb-4">
              Need to run your own draw?
            </p>
            <Button onClick={() => window.location.href = '/drawings'}>
              Create Your Own Draw
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SharedDraw;
